import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Observable, map, share, tap, timeout, throwError, of } from 'rxjs';
import { MyCampAppConstants } from './constants';
import { ApiEndpointInfo } from './api-endpoint-info.model';
import { MyCampApp } from './enumerations';
import { environment } from '../../../environments/environment';
import { ApiServiceCacheItem } from './api-service-cache-item';
import { CachedValue } from './cached-value';
import { ApiCachedRequestResponse } from './api-cached-request-response';
import { catchError } from 'rxjs/operators';

export enum HttpMethod
{
	Get,
	Post,
	Put,
	Patch,
	Delete
}

@Injectable({
	providedIn: 'root'
})
export class ApiService
{
	// Cache API requests to avoid multiple calls to APIs. This is a dictionary of information based on the URL and contains:
	// observable: Observable<any> if the request is already in progress
	// result: any if the request is already complete
	private currentApiRequests: ApiServiceCacheItem<any>[] = [];

	private defaultEndpointIndex = environment.defaultApiEndpoint;
	private apiEndpoints: ApiEndpointInfo[] = [
		new ApiEndpointInfo(MyCampApp.Environment.Dev, 'https://localhost:5001'),
		new ApiEndpointInfo(MyCampApp.Environment.Prod, 'https://apiv2.my1218app.com'),
		new ApiEndpointInfo(MyCampApp.Environment.Staging, 'https://apiv2staging.my1218app.com'),
		new ApiEndpointInfo(MyCampApp.Environment.Test, 'https://apiv2test.my1218app.com'),
		new ApiEndpointInfo(MyCampApp.Environment.CampSiteBeta, 'https://apicsbeta.my1218app.com'),
	];

	protected baseUrl = this.apiEndpoints.find(e => e.environment === this.defaultEndpointIndex)!.baseUrl;
	protected get baseApiUrl(): string { return `${this.baseUrl}/api/`; }
	protected get baseAuthApiUrl(): string { return `${this.baseApiUrl}auth`; }

	public static defaultPageSize = MyCampAppConstants.defaultDataPageSize;

	private readonly headers = new HttpHeaders().set('Content-Type', 'application/json');

	constructor(protected http: HttpClient)
	{
	}

	public static getMessage(error: HttpErrorResponse): string
	{
		if (error)
		{
			if (error.error && Object.keys(error.error).length > 0)
			{
				const keys = Object.keys(error.error);
				return error.error[ keys[ 0 ] ];
			}

			if (error.message) return error.message;
		}

		return 'Unknown error';
	}

	static isValidUploadFilename(filename: string): boolean
	{
		const forbiddenChars = /^[^\\/:\*\?"<>\|\^]+$/;
		return forbiddenChars.test(filename);
	}

	apiRequest<T>(url: string, httpMethod: HttpMethod, httpBody?: string): Observable<T>
	{
		// Only allow caching of GET calls
		if (httpMethod !== HttpMethod.Get) return this.http.request<T>(HttpMethod[httpMethod], url, { headers: this.headers, body: httpBody });

		return this.apiRequestWithCache<T>(url, 2).pipe(map(result => result.value));
	}

	apiRequestWithCache<T>(url: string, cacheDurationSeconds?: number): Observable<ApiCachedRequestResponse<T>>
	{
		// See if we already have an entry for this URL and if it is currently in-progress
		let entry = this.currentApiRequests.find(c => c.url === url);
		if (entry?.observable !== undefined) return entry.observable;

		// Add an entry to the list. Note: this is guaranteed to be populated below with an observable before we return
		if (entry === undefined)
		{
			entry = new ApiServiceCacheItem(url);
			this.currentApiRequests.push(entry);
		}

		// See if we have a cached value (and we are allowing using the cached value)
		if (entry.cachedValue !== undefined && !entry.cachedValue!.isExpired) {
			entry.cachedResponse!.isFromCache = true;
			return of(entry.cachedResponse!);
		}

		// Make the call. Cache the result (if desired) and handle errors by eliminating the URL from the list
		const observable = this.http.request<T>(HttpMethod[HttpMethod.Get], url, { headers: this.headers })
			.pipe(share(),
				tap((result: any) =>
				{
					entry!.observable = undefined;
					entry!.cachedValue = new CachedValue<any>(result, cacheDurationSeconds);
					entry!.cachedResponse = new ApiCachedRequestResponse(entry!.cachedValue.value, false);
				}),
				map(() => entry!.cachedResponse!),
				catchError((error: any) =>
				{
					this.currentApiRequests = this.currentApiRequests.filter(r => r.url !== url);
					return throwError(error);
				}));
		entry.observable = observable;
		return observable;
	}

	apiGetRequestForBlob<T>(url: string): Observable<T>
	{
		const headers = new HttpHeaders().set('Content-Type', 'application/json').append('Accept', 'image/jpeg');
		return this.http.get<T>(url, { headers: headers, responseType: 'blob' as 'json' })
			.pipe(timeout(15000));
	}

	apiGetRequestForCsv<T>(url: string): Observable<T>
	{
		const headers = new HttpHeaders().set('Content-Type', 'application/json').append('Accept', 'text/csv');
		return this.http.get<T>(url, { headers: headers, responseType: 'blob' as 'json' })
			.pipe(timeout(90000));
	}

	apiPostRequestForCsv<T>(url: string, httpBody?: string): Observable<T>
	{
		const headers = new HttpHeaders().set('Content-Type', 'application/json').append('Accept', 'text/csv');
		return this.http.post<T>(url, httpBody, { headers: headers, responseType: 'blob' as 'json' })
			.pipe(timeout(90000));
	}

	apiUploadRequest<T>(url: string, formData: FormData, timeoutInSecs: number, headers?: HttpHeaders, responseType?: any)
	{
		var options: any = { };
		if (headers !== undefined && headers !== null) { options.headers = headers; }
		if (responseType !== undefined && responseType !== null) { options.responseType = responseType; }
		return this.http.post<T>(url, formData, options)
			.pipe(timeout(timeoutInSecs * 1000));
	}

	apiRequestWithProgress(url: string, httpMethod: HttpMethod, httpBody?: string): Observable<any>
	{
		const req = new HttpRequest(HttpMethod[ httpMethod ], url, httpBody, { headers: this.headers, reportProgress: true });
		return this.http.request(req);
	}

	protected getListQueryParams(defaultSortField: string, pageIndex?: number, pageSize?: number, sortField?: string, sortOrder?: MyCampApp.SortOrder)
	{
		sortField = sortField ? sortField : defaultSortField;

		return `sortField=${sortField}&sortOrder=${this.getSortOrder(sortOrder)}&pageIndex=${pageIndex}&pageSize=${pageSize}`;
	}

	private getSortOrder(sortOrder?: MyCampApp.SortOrder): string
	{
		sortOrder = sortOrder ? sortOrder : MyCampApp.SortOrder.Ascending;
		return (sortOrder === MyCampApp.SortOrder.Ascending) ? 'asc' : 'desc';
	}
}
